import type { HardwareRevision, Issue, Job, ProjectHome, Review } from "./api.ts";
import { jobType, shortOid } from "./api.ts";
import type { WorkflowStageId } from "./workflow.ts";

export type StageStatus = "done" | "current" | "next";

export type NextUp = {
  title: string;
  body: string;
  primary: { label: string; to: string };
  secondary: { label: string; to: string };
};

const STAGE_ORDER: WorkflowStageId[] = ["setup", "spec", "design", "verify", "review", "track"];

function succeededVerify(jobs: Job[]): boolean {
  return jobs.some(
    (j) =>
      j.state === "succeeded" &&
      (j.kind === "import_kicad" || jobType(j.kind, j.kind_detail) === "circuit_diff"),
  );
}

/** Live evidence that each stage 01–06 has been entered. Later evidence backfills earlier stages. */
function evidence(home: ProjectHome | null): boolean[] {
  return [
    true,
    (home?.requirements ?? []).length > 0,
    (home?.hardware ?? []).length > 0,
    succeededVerify(home?.jobs ?? []),
    (home?.reviews ?? []).length > 0,
    (home?.issues ?? []).length > 0,
  ];
}

function completedFromEvidence(flags: boolean[]): boolean[] {
  const complete = flags.map(() => false);
  let later = false;
  for (let i = flags.length - 1; i >= 0; i--) {
    later = later || flags[i] === true;
    complete[i] = later;
  }
  return complete;
}

function pickCurrent(complete: boolean[], flags: boolean[]): WorkflowStageId {
  const firstIncomplete = complete.findIndex((c) => !c);
  if (firstIncomplete < 0) return "track";
  const id = STAGE_ORDER[firstIncomplete];
  // Review stays current until an issue exists; Track is the follow-on.
  if (id === "track" && flags[STAGE_ORDER.indexOf("review")]) return "review";
  return id;
}

export function currentStage(home: ProjectHome | null): WorkflowStageId {
  const flags = evidence(home);
  return pickCurrent(completedFromEvidence(flags), flags);
}

export function stageStatuses(home: ProjectHome | null): Record<WorkflowStageId, StageStatus> {
  const current = currentStage(home);
  const ci = STAGE_ORDER.indexOf(current);
  const out = {} as Record<WorkflowStageId, StageStatus>;
  for (let i = 0; i < STAGE_ORDER.length; i++) {
    out[STAGE_ORDER[i]] = i < ci ? "done" : i === ci ? "current" : "next";
  }
  return out;
}

export function latestHardware(home: ProjectHome | null): HardwareRevision | null {
  return home?.hardware?.[0] ?? null;
}

export function latestReview(home: ProjectHome | null): Review | null {
  return home?.reviews?.[0] ?? null;
}

export function latestJob(home: ProjectHome | null): Job | null {
  return home?.jobs?.[0] ?? null;
}

export function latestIssue(home: ProjectHome | null): Issue | null {
  return home?.issues?.[0] ?? null;
}

export function hardwareLabel(rev: HardwareRevision | null): string {
  if (!rev) return "no revision";
  const label = (rev.label || "").trim() || shortOid(rev.git_oid);
  return `${label} ${shortOid(rev.git_oid)}`;
}

/** Project URL prefix like `/o/pcbx/p/slug`. Never stringifies null/undefined into the path. */
export function normalizeProjectBase(base: string | null | undefined): string {
  if (typeof base !== "string") return "";
  const trimmed = base.trim().replace(/\/+$/, "");
  if (!trimmed || trimmed === "undefined" || trimmed === "null") return "";
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

export function nextUp(home: ProjectHome | null, base: string | null | undefined): NextUp {
  const root = normalizeProjectBase(base);
  const stage = currentStage(home);
  const hw = latestHardware(home);
  const review = latestReview(home);
  const issue = latestIssue(home);
  const upload = { label: "Upload another zip", to: `${root}/files#upload` };
  const reviewHref = review
    ? `${root}/changes/${review.id}?from_ref=${encodeURIComponent(review.from_ref)}&to_ref=${encodeURIComponent(review.to_ref)}`
    : `${root}/changes?new=1`;
  const reviewTitle = review ? review.title || `${review.from_ref} → ${review.to_ref}` : "";

  if (stage === "setup") {
    return {
      title: "Finish project setup",
      body: "Stage 01 Setup is current. This project hosts on-platform (D1 + R2 + libgit2). Confirm settings, then upload a KiCad zip — GitHub is optional sync, not the source of truth.",
      primary: { label: "Open Settings", to: `${root}/settings` },
      secondary: { label: "Upload KiCad zip", to: `${root}/files#upload` },
    };
  }

  if (stage === "spec") {
    return {
      title: "Capture requirements",
      body: "Stage 02 Spec has no requirements yet. Record the board brief before design.",
      primary: { label: "Open Requirements", to: `${root}/requirements` },
      secondary: { label: "Open Settings", to: `${root}/settings` },
    };
  }

  if (stage === "design") {
    return {
      title: "Upload KiCad zip",
      body: "No hosted hardware revision yet. Stage 03 needs a zip before review.",
      primary: { label: "Upload KiCad zip", to: `${root}/files#upload` },
      secondary: { label: "Open Files", to: `${root}/files` },
    };
  }

  if (stage === "verify") {
    return {
      title: "Verify the latest revision",
      body: `Hardware ${hardwareLabel(hw)} is in SoT. Import / circuit_diff has not succeeded yet — stage 04 is current.`,
      primary: { label: "Open Jobs", to: `${root}/jobs` },
      secondary: upload,
    };
  }

  if (stage === "track") {
    return {
      title: "Track open issues",
      body: issue
        ? `Hardware ${hardwareLabel(hw)} is in SoT.${review ? ` Review “${reviewTitle}” is open · ` : " "}${issue.title}.`
        : `Hardware ${hardwareLabel(hw)} is in SoT. Stage 06 Track is current.`,
      primary: issue
        ? { label: "Open issue", to: `${root}/issues/${issue.id}` }
        : { label: "Open Issues", to: `${root}/issues` },
      secondary: review
        ? { label: "Open design review", to: reviewHref }
        : upload,
    };
  }

  if (!review) {
    return {
      title: "Open design review",
      body: `Hardware ${hardwareLabel(hw)} is in SoT. Design review not opened yet — stage 05 is ready.`,
      primary: { label: "Open design review", to: `${root}/changes?new=1` },
      secondary: upload,
    };
  }

  return {
    title: "Open design review",
    body: `Hardware ${hardwareLabel(hw)} is in SoT. Review “${reviewTitle}” is open.`,
    primary: { label: "Open design review", to: reviewHref },
    secondary: upload,
  };
}
